import React from 'react';
import loadable from '@loadable/component';
import DOMPurify from 'dompurify';
import { axiosInstance as axios } from 'Api';
import axiosBase from 'axios'
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { InAppBrowser } from '@capacitor/inappbrowser';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { FirebaseMessaging } from 'merchant-product-push-plugin';
import { Preferences } from '@capacitor/preferences';
import CodiceFiscale from 'codice-fiscale-js';

/** @function
 * * Handling the API Rest resposnse, and return data and different message for status code
 * @name handlingResponse
 * @param {object} data - The response of method
 * @return {object} - The response of method, with additional info
 */
export const handlingResponse = (data) => {
  const response = { error: '' };

  // Gestione richieste cancellate dal requestManager
  // Ritorna un errore speciale che verrà ignorato dal middleware e dal loading
  if (data && data._cancelled) {
    return { error: { cancelled: true, status: 0 }, response: null };
  }

  if (data && data.status !== 200 && data.status !== 204) {
    switch (data.status) {
      case 401:
        data.type = 'Unauthorized';
        break;
      case 404:
        data.type = 'Not Found';
        break;
      case 500:
        data.type = 'ServerError';
        break;
      default:
        data.type = 'Invalid Data';
        break;
    }
    return { response, error: data };
  }
  return { response: data };
};

/**  @function
 * * Handling the null case and set to safe
 *
 * @name nullSafe
 * @param {function} func
 * @param {*} fallbackValue
 * @returns {*} the value to set safe the method
 */
export const nullSafe = (func, fallbackValue) => {
  try {
    const value = func();
    return value === null || value === undefined ? fallbackValue : value;
  } catch (e) {
    return fallbackValue;
  }
};

/**  @function
 * * Handling the null case and null strings and set to safe
 *
 * @name nullSafeString
 * @param {function} func
 * @param {*} fallbackValue
 * @returns {*} the value to set safe the method, also checking for "null" and "Null" strings
 */
export const nullSafeString = (func, fallbackValue) => {
  try {
    const value = func();
    // Check for null, undefined, and string representations of null
    if (value === null || value === undefined || value === 'null' || value === 'Null') {
      return fallbackValue;
    }

    // Check if it's a string that contains only null-like values, whitespace, and punctuation
    if (typeof value === 'string') {
      // Remove all whitespace, commas, and dashes, then check if what remains is only null-like values
      const cleaned = value.replace(/[\s,\-_]/g, '').toLowerCase();
      const nullPattern = /^(null)*$/;

      if (cleaned === '' || nullPattern.test(cleaned)) {
        return fallbackValue;
      }
    }

    return value;
  } catch (e) {
    return fallbackValue;
  }
};

/**  @function
 * * Import a component with loadable library
 *
 * @name loadable
 * @returns {$Symbols} the required component
 */
// export const loadableComponent = (component) =>
//   loadable(
//     () =>
//       import(
//         /* webpackChunkName: "[request]" */
//         /* webpackExclude: /\.test\.(js|jsx)$/ */
//         `Components/${component}`
//       ),
//     {
//       fallback: <div> Loading... </div>,
//     }
//   );

/**  @function
 * * Import a page with loadable library
 *
 * @name loadable
 * @returns {$Symbols} the required library
 */
export const LoadablePage = loadable(
  (props) =>
    import(
      /* webpackChunkName: "[request]" */
      /* webpackExclude: /(\.test\.(js|jsx)|\.md)$/ */
      `Route/${props.page}/components/${props.page}Container`
    ),
  {
    cacheKey: (props) => props.page,
  }
);

export const LoadableSubPage = loadable(
  (props) =>
    import(
      /* webpackChunkName: "[request]" */
      /* webpackExclude: /(\.test\.(js|jsx)|\.md)$/ */
      `Route/${props.father ? `${props.father}/` : ''}components/${props.page}/${props.subpage ? `${props.subpage}/${props.subpage}Container` : `${props.page}Container`
      }`
    ),
  {
    cacheKey: (props) => props.page + (props.subpage ? `-${props.subpage}` : ''),
  }
);

/**  @function
 * * Retrieve the desired value, from an object
 *
 * @name getNestedObject
 * @param {object} nestedObj
 * @param {array} pathArr
 * @returns {*} the value from the chhosed key
 */

export const getNestedObject = (nestedObj, pathArr) =>
  pathArr.reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), nestedObj);

/**
 ** Sanitize the input and return it
 * @param {any} params
 * @returns {any}
 */

export const sanitizeInput = (params) => {
  let objectSanitized = Object.assign({});
  switch (typeof params) {
    case 'object':
      if (Array.isArray(params)) {
        objectSanitized = [];
        params.forEach((v) => {
          if (typeof v === 'object' && v !== null) {
            const obj = {};
            for (const a in v) {
              obj[a] = typeof v[a] === 'string' ? DOMPurify.sanitize(v[a]) : v[a];
            }
            objectSanitized.push(obj);
          } else {
            objectSanitized.push(typeof v === 'string' ? DOMPurify.sanitize(v) : v);
          }
        });
      } else {
        for (const a in params) {
          if (Array.isArray(params[a])) {
            const obj = [];
            params[a].forEach((v) => {
              obj.push(typeof v === 'string' ? DOMPurify.sanitize(v) : v);
            });
            objectSanitized[a] = obj;
          } else {
            objectSanitized[a] = typeof params[a] === 'string' ? DOMPurify.sanitize(params[a]) : params[a];
          }
        }
      }
      return objectSanitized;

    default:
      return typeof params === 'string' ? DOMPurify.sanitize(params) : params;
  }
};

/**  @function
 * * Validate the access_token
 *
 * @name validateJWT
 * @returns {object} the response object
 */

export async function validateJWT() {
  let data;

  try {
    data = await axios({
      method: 'post',
      url: `${process.env.logger_server}/validateToken`,
    });
    return handlingResponse(data);
  } catch (error) {
    return handlingResponse(error);
  }
}

/**  @function
 * Open external link
 */

export const openUrl = (url, newTab) => {
  if (Capacitor.isNativePlatform()) {
    InAppBrowser.openInExternalBrowser({ url: url })
      .then(() => {
        try {
          if (typeof window !== 'undefined' && typeof window.restoreStatusBarSafe === 'function') {
            window.restoreStatusBarSafe();
          }
        } catch (e) {
          console.warn('[openUrl] restoreStatusBarSafe call failed', e);
        }
      })
      .catch(() => {
        try {
          if (typeof window !== 'undefined' && typeof window.restoreStatusBarSafe === 'function') {
            window.restoreStatusBarSafe();
          }
        } catch (e) {
          console.warn('[openUrl] restoreStatusBarSafe call failed', e);
        }
      });
  } else {
    if (newTab) {
      window.open(url, '_blank', 'noreferrer');
    } else {
      window.open(url, '_self', 'noreferrer');
    }
  }
};

export const detectPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera

  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return 'ios'
  }

  if (/android/i.test(userAgent)) {
    return 'android'
  }

  return 'web'
}

/**  @function
 * download file
 * @param {string|ArrayBuffer} data - The data to be downloaded, can be a string or an ArrayBuffer.
 * @param {string} name - The name of the file to be downloaded.
 * @param {string} mimeType - The MIME type of the file to be downloaded.
 * @returns {Promise<void>} - A promise that resolves when the file is downloaded.
 * @description
 * This function downloads a file with the specified data, name, and MIME type.
 * It handles both web and native platforms (iOS and Android).
 * If the platform is native, it checks and requests permissions to write files.
 * If the platform is web, it creates a link element to trigger the download.
 * It also supports different MIME types and file extensions.
 * 
 * @example
 * downloadFile('Hello World', 'example.txt', 'text/plain');
 * downloadFile(new Uint8Array([72, 101, 108, 108, 111]), 'example.bin', 'application/octet-stream');
 * downloadFile(pdfData, 'document.pdf', 'application/pdf');
 * downloadFile(imageData, 'image.png', 'image/png');
 */

export const downloadFile = async (data, name, mimeType, analyticsLabel) => {
  const blob = new Blob([data], { type: mimeType });
  if (Capacitor.isNativePlatform()) {
    const checkPermissions = await checkAndRequestPermissions();
    if (checkPermissions) {
      const reader = new FileReader();
      let base64Data;
      reader.onloadend = async () => {
        if (typeof data === "string" && /^[A-Za-z0-9+/]+=*$/.test(data.substr(0, 50))) {
          base64Data = data;
        } else {
          base64Data = reader.result.split(',')[1];
        }
        const pathForSave = Directory.Documents;
        const fileName = `${name}.${getExtensionFromMimeType(mimeType)}`;

        await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: pathForSave,
        });

        const { uri } = await Filesystem.getUri({
          path: fileName,
          directory: pathForSave,
        });

        const fileOpenerOptions = {
          filePath: uri,
          contentType: mimeType,
          openWithDefault: true,
        };

        try {
          await FileOpener.open(fileOpenerOptions);
        } catch (err) {
          console.warn('[FileOpener] open failed', err);
        } finally {
          try {
            if (typeof window !== 'undefined' && typeof window.restoreStatusBarSafe === 'function') {
              // call without awaiting to avoid blocking; AppInit will retry internally
              window.restoreStatusBarSafe();
            }
          } catch (e) {
            console.warn('[FileOpener] restoreStatusBarSafe call failed', e);
          }
        }
      };

      reader.readAsDataURL(blob);
    }
  } else {
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);

    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }

  // Analytics: prefer shared helper so we include alwaysPresentParams
  if (typeof window !== 'undefined') {
    const extension = getExtensionFromMimeType(mimeType);
    const downloadLabel = analyticsLabel || name || '';
    try {
      const { setDownloadAnalytics } = require('Store/middleware/analytics');
      if (typeof setDownloadAnalytics === 'function') {
        setDownloadAnalytics(extension, downloadLabel);
        return;
      }
    } catch (error) {
      // fallback below
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'custom_download',
        download: {
          extension,
          label: downloadLabel
        }
      })
    }
  }
};

/**  @function
 * check and ask permissions for files in IOS and Android app
 */
const checkAndRequestPermissions = async () => {
  const status = await Filesystem.checkPermissions();

  if (status.publicStorage !== 'granted') {
    const requestStatus = await Filesystem.requestPermissions();

    if (requestStatus.publicStorage !== 'granted') {
      console.log('Permessi di scrittura non concessi');
      return false;
    }
    return true;
  }

  return true;
};

/**  @object
 * map mimeType to Extension
 */
const mimeToExtensionMap = {
  // Text
  'text/plain': 'txt',
  'text/html': 'html',
  'text/css': 'css',
  'text/csv': 'csv',
  'text/javascript': 'js',
  'application/javascript': 'js',
  'application/json': 'json',
  'application/xml': 'xml',

  // Images
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff',

  // Audio
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/webm': 'weba',

  // Video
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/ogg': 'ogv',

  // Documents
  'application/pdf': 'pdf',
  'application/zip': 'zip',
  'application/x-rar-compressed': 'rar',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',

  // Binary
  'application/octet-stream': 'bin',

  // Form
  'application/x-www-form-urlencoded': 'urlencoded',
  'multipart/form-data': 'form',
};

/**  @function
 * return extension from mimeType
 */
export function getExtensionFromMimeType(mimeType) {
  return mimeToExtensionMap[mimeType.toLowerCase()] || '';
}

/**  @function
 * replace dot with comma
 */

export function replaceDotWithComma(value) {
  if (typeof value === 'number') {
    value = value.toString();
  } else if (typeof value !== 'string') {
    return value;
  }
  return value.replace(/\./g, ',');
}

/**  @function
 * replace comma with dot
 */

export function replaceCommaWithDot(value) {
  if (typeof value === 'number') {
    value = value.toString();
  } else if (typeof value !== 'string') {
    return value;
  }
  return value.replace(/,/g, '.');
}

/**  @function
 * formatValue
 * console.log(formatValue(0.546, 2)); // Output: 0.55
 * console.log(formatValue('0.546', 3)); // Output: "0.546"
 * console.log(formatValue('0,546', 4)); // Output: "0,5460"
 */

export function formatValue(value, decimals) {
  if (value === null || value === undefined) {
    return value;
  }
  // Set default value for decimals if undefined or null
  decimals = decimals === undefined || decimals === null ? 2 : decimals;

  const stringValue = typeof value === 'number' ? value.toString() : value;
  const hasComma = stringValue.includes(',');

  const normalizedValue = stringValue.replace(',', '.');
  const parsedValue = parseFloat(normalizedValue);

  if (!isNaN(parsedValue)) {
    const roundedValue = parsedValue.toFixed(decimals);
    const formattedValue = hasComma ? roundedValue.replace('.', ',') : roundedValue;
    return typeof value === 'number' ? parseFloat(formattedValue) : formattedValue;
  }
}

/**  @function
 * capitalizeFirstLetter
 */

export function capitalizeFirstLetter(string) {
  if (string == null || typeof string !== 'string') {
    return string;
  }
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

/**  @function
 * truncate string with ellipsis
 */

export function truncateString(str, maxLength) {
  const ellipsis = '...';
  if (!str) {
    return '';
  }
  if (str.length > maxLength) {
    return str.substring(0, maxLength - ellipsis.length) + ellipsis;
  }
  return str;
}

/** @function
 * * Generate a test for formik
 */
export const generateFormikTest = (testName, errorMessage, testFunction) => ({
  name: `${testName}Validation`,
  message: errorMessage,
  test: testFunction,
});

/**  @function
 * Script Contained
 */

export const scriptContained = (url) => document.querySelectorAll(`script[src*="${url}"]`).length > 0;

/**  @function
 * retrieve only the error number from the BE error code
 * example: "1.CoBa.web.de" => "1"
 * @name getErrorNumber
 * @param {string} error - The error code
 * @return {string} - The error number
 *
 */

export const getErrorNumber = (error) => {
  if (!error) {
    return null;
  }
  const errorNumber = error.split('.')[0];
  return errorNumber;
};

/**
 * Checks if a given value is a valid date in the format 'DD/MM/YYYY'.
 *
 * @param {string} value - The value to be checked.
 * @returns {boolean} - Returns true if the value is a valid date, false otherwise.
 */
export const isValidDate = (value, format = 'DD/MM/YYYY') => (value ? moment(value, format, true).isValid() : false);

/**
 * Calculates the difference in years between a given date and the current date.
 * @param {string} date - The date to calculate the difference from.
 * @param {string} [format='DD/MM/YYYY'] - The format of the date string.
 * @returns {number} - The difference in years between the two dates.
 */
export const differenceInYear = (date, format = 'DD/MM/YYYY') =>
  moment(moment(), format).diff(moment(date, format), 'years', true);

/**
 * Removes duplicates from an array of objects based on a specified property.
 *
 * @param {Array} arr - The array of objects.
 * @param {string} property - The property to check for duplicates.
 * @returns {Array} - The array with duplicates removed.
 */
export const removeDuplicatesFromObjectArray = (arr, property) => {
  if (!property || arr.length === 0) return [];

  const unseenValues = new Set();
  const uniqueValues = [];
  for (let obj of arr) {
    if (!unseenValues.has(obj[property])) {
      unseenValues.add(obj[property]);
      uniqueValues.push(obj);
    }
  }

  return uniqueValues;
};

/**
 * Subtracts two numeric strings and returns the result as a formatted string.
 * @param {string} strNum1 - The first numeric string ("72.777,00").
 * @param {string} strNum2 - The second numeric string ("10,02").
 * @returns {string} The result of subtracting strNum2 from strNum1 as a formatted string
 * example console.log(subtractNumericStrings("72.772,76", "10,02"));  // should print "72.762,74".
 */
export const subtractNumericStrings = (strNum1, strNum2) => {
  // Convert strings to numbers, replacing dots with nothing and commas with dots
  let num1 = parseFloat(strNum1.replace('.', '').replace(',', '.'));
  let num2 = parseFloat(strNum2.replace('.', '').replace(',', '.'));

  // Perform the subtraction
  let result = num1 - num2;

  // Convert the result to string and format with two decimal places
  let strResult = result.toFixed(2);

  // Add the thousands separator
  strResult = strResult.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  // Replace the last dot with a comma
  let lastDot = strResult.lastIndexOf('.');
  if (lastDot != -1) {
    strResult = strResult.substr(0, lastDot) + ',' + strResult.substr(lastDot + 1);
  }

  return strResult; // should print "72.762,74"
};

/**
 * Check if a version (v1) is greater or equal than a reference version(v2).
 * @param {string} v1 - The version number that we must check.
 * @param {string} v2 - The reference version.
 * @returns {boolean} The result of v1>=v2
 */
export const isVersionGreaterOrEqual = (v1, v2) => {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  const length = Math.max(parts1.length, parts2.length);

  for (let i = 0; i < length; i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;
    if (num1 > num2) return true;
    if (num1 < num2) return false;
  }

  return true;
};

/**
 * Formats a number by adding thousands separators.
 *
 * @param {number} number - The number to be formatted.
 * @returns {string} The formatted number with thousands separators.
 * example console.log(formatThousands(1000.69));  // Prints: "1.000,69"
 */
export const formatThousands = (number) => {
  var parts = number.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.join(',');
};

/**
 * Formats the given amount by removing leading zeros and adding a leading zero if necessary.
 *
 * @param {string} amount - The amount to be formatted.
 * @returns {string} The formatted amount.
 * example:
 * console.log(formatAmount("0001,00"));  // Output: "1,00"
   console.log(formatAmount("000,09"));   // Output: "0,09"
   console.log(formatAmount("0123,45"));  // Output: "123,45"
   console.log(formatAmount("0000,10"));  // Output: "0,10"
   console.log(formatAmount("0000,00"));  // Output: "0,00"
 */
export const formatAmount = (amount) => amount.replace(/^0+(?=[1-9])/, '').replace(/^0+(\d)/, '0$1');

/**
 * Merges the properties of two or more objects into the first object.
 * If the objects have nested objects, the nested objects are merged recursively.
 * @param {object} target - The object to merge the other objects into.
 * @param {object[]} sources - The objects to merge into the target object.
 * @returns {object} The target object with the merged properties.
 */

export function merge(target, ...sources) {
  if (!sources.length) return target;
  const source = sources.shift();

  for (const key in source) {
    if (source[key] instanceof Object) {
      if (!target[key]) {
        target[key] = {};
      }
      merge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }

  return merge(target, ...sources);
}

const mobileCookieSet = async (name, value) => {
  await Preferences.set({ key: name, value: value })
}

export async function setCookie(name, value, origin = '') {
  if (Capacitor.isNativePlatform()) {
    await mobileCookieSet(name, value)
  } else {
    // Fix set cookie domain not working for localhost
    const domain = origin && origin.includes('localhost') ? '' : process.env.COOKIE_DOMAIN;

    const d = new Date();
    d.setTime(d.getTime() + 1000 * 60);
    document.cookie = `${name}=${value}; Expires=${d.toUTCString()}; Path=/; Domain=${domain};`;
  }
}

export function setCookieSync(name, value, origin = '') {
  // Fix set cookie domain not working for localhost
  const domain = origin && origin.includes('localhost') ? '' : process.env.COOKIE_DOMAIN;

  const d = new Date();
  d.setTime(d.getTime() + 1000 * 60);
  document.cookie = `${name}=${value}; Expires=${d.toUTCString()}; Path=/; Domain=${domain};`;
}

const mobileCookieDelete = async (name) => {
  await Preferences.remove({ key: name });
};

export async function expireCookie(name, origin = '') {
  if (Capacitor.isNativePlatform()) {
    await mobileCookieDelete(name);
  } else {
    // Fix expire cookie domain not working for localhost
    const domain = origin && origin.includes('localhost') ? '' : process.env.COOKIE_DOMAIN;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT; Path=/; Domain=${domain};`;
  }
}

export function expireCookieSync(name, origin = '') {
  // Fix expire cookie domain not working for localhost
  const domain = origin && origin.includes('localhost') ? '' : process.env.COOKIE_DOMAIN;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT; Path=/; Domain=${domain};`;
}

/**
 * Calculates the sum of a specific property in an object.
 *
 * @param {object} obj - The object containing the data.
 * @param {string} property - The name of the property to sum.
 * @returns {number} - The total sum of the specified property.
 * example:
 * const obj = {
 * item1: { value: 100 },
 * item2: { value: 200 },
 * item3: { value: 300 }
 * };
 * getTotalPropertySum(obj, 'value'); // Output: 600
 * getTotalPropertySum(obj, 'price'); // Output: 0
 */
export function getTotalPropertySum(obj, property) {
  if (!obj || typeof obj !== 'object' || !property) {
    return; // Returns if the input is invalid
  }

  return Object.values(obj).reduce(
    (total, item) => total + (item[property] || 0), // Adds the specified property, defaults to 0 if undefined
    0
  );
}

/**
 * Sanitize the given string from HTML tags.
 *
 * @param {str} str - The string to sanitize.
 * example:
 * const str = "<strong>Hello</strong> <em>World</em>";
 * const sanitizedStr = removeHtmlTagsFromString(str);
 * console.log(sanitizedStr); // Output: "Hello World"
 */
export function removeHtmlTagsFromString(str) {
  return str.replace(/<[^>]*>/g, '');
}

const mobileCookieGet = async (nome) => {
  const stored = await Preferences.get({ key: nome });
  let value = stored.value;
  return value;
};

/**
 * Retrieves the value of a specified cookie by its name.
 *
 * @param {string} nome - The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie if found, or null if the cookie does not exist.
 */
export async function getCookie(nome) {
  let value = null;
  if (Capacitor.isNativePlatform()) {
    return await mobileCookieGet(nome);
  } else {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const c = cookies[i].trim();
      if (c.startsWith(nome + '=')) {
        value = c.substring((nome + '=').length, c.length);
      }
    }
  }
  return value;
}

export function getCookieSync(nome) {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const c = cookies[i].trim();
    if (c.startsWith(nome + '=')) {
      const value = c.substring((nome + '=').length, c.length);
      return value;
    }
  }
  return null;
}

/**
 * Formats the given amount string by replacing dots with commas and validating it
 * based on the allowed number of integer and decimal digits.
 *
 * @param {string} amount - The raw amount string to be formatted.
 * @param {number} [int=6] - Maximum number of digits allowed before the comma.
 * @param {number} [decimals=2] - Maximum number of digits allowed after the comma.
 * @param {boolean} [allowNegative=false] - Whether to allow negative values.
 *
 * @returns {{ value: string, isValid: boolean }} An object containing the formatted value
 *          and a boolean indicating whether it matches the expected numeric format.
 *
 * @example
 * console.log(formatAmountValue("10,5"));   // { value: "10,5", isValid: true }
 * console.log(formatAmountValue("10.5"));   // { value: "10,5", isValid: true }
 * console.log(formatAmountValue("-10.5", 6, 2, true)); // { value: "-10,5", isValid: true }
 * console.log(formatAmountValue("1000000")); // { value: "1000000", isValid: false }
 */
export function formatAmountValue(amount, int = 6, decimals = 2, allowNegative = false) {
  const amountRegex = new RegExp(`${allowNegative ? '^-?' : '^'}\\d{0,${int}}(,\\d{0,${decimals}})?$`);
  const formattedAmount = amount.replace('.', ',');

  return {
    isValid: formattedAmount === '' || amountRegex.test(formattedAmount),
    value: formattedAmount
  };
}

/**
 * Formats the given amount by removing all characters other than digits and commas and replace dots with commas.
 *
 * @param {string} amountString - The amount to be formatted.
 *
 * example:
 * console.log(formatAmount("€ 10,50"));  // Output: "10,50"
 * console.log(formatAmount("€ 10.50"));  // Output: "10,50"
 */
export function cleanAmountString(amountString) {
  return amountString.replace(/\./g, ',').replace(/[^\d,\-]/g, '');
}

/**
 * Format a numeric string into “n,nn” form and optionally wrap it with a currency symbol.
 *
 * @param {string} amount         – the raw input (e.g. "10,5", "€ 123", etc.)
 * @param {string} symbol         – the currency symbol to use (default: "€")
 * @param {boolean} previousSymbol – if true, put symbol before the number; if false, put it after (default: true)
 * @param {boolean} showSymbol    – if false, omit the symbol entirely (default: true)
 * @returns {string}              – formatted string (e.g. "€ 10,50" or "10,50 €" or "10,50")
 */
export function formatAmountString(amount, symbol = '€', previousSymbol = true, showSymbol = true) {
  const raw = cleanAmountString(amount);
  if (!raw) return '';

  let [intPart, decPart = ''] = raw.split(',');

  if (decPart.length === 0) decPart = '00';
  else if (decPart.length === 1) decPart = decPart + '0';
  else decPart = decPart.slice(0, 2);

  const amountString = `${intPart},${decPart}`;

  if (!showSymbol) {
    return amountString;
  }

  const nbsp = '\u00A0';
  if (previousSymbol) {
    return `${symbol}${nbsp}${amountString}`;
  } else {
    return `${amountString}${nbsp}${symbol}`;
  }
}

/**
 * Convert the given amountString to float.
 *
 * @param {string} amountString - The amount to be converted.
 *
 * example:
 * console.log(formatAmount("€ 10,50"));  // Output: 10.50
 * console.log(formatAmount("10,50"));  // Output: 10.50
 */
export function convertAmountToFloat(amountString) {
  return parseFloat(cleanAmountString(amountString).replace(',', '.'));
}

/**
 * Convert the given dateIsoString to format dd/mm/aaaa.
 *
 * @param {string} dateString - The date to be converted.
 *
 * example:
 * console.log(formatDateByIso("2025-04-29T10:30:00"));  // Output: 29/04/2025
 */
export function formatDateByIso(dateString) {
  const date = new Date(dateString);

  // Ottenere giorno, mese e anno
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() ritorna l'indice del mese, quindi aggiungiamo 1
  const year = date.getFullYear();

  // Formattare la data come 'dd/mm/yyyy'
  const formattedDate = `${day}/${month}/${year}`;

  return formattedDate;
}
/**
 * Convert the given dateIsoString to format dd/mm/aaaa - hh:mm.
 *
 * @param {string} dateString - The date to be converted.
 *
 * example:
 * console.log(formatDateAndHourByIso("2025-04-29T10:30:00"));  // Output: 29/04/2025
 */
export function formatDateAndHourByIso(dateString) {
  const date = new Date(dateString);

  // Ottenere giorno, mese e anno
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() ritorna 0-11
  const year = date.getFullYear();

  // Ottenere ore e minuti
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  // Formattare come 'dd/mm/yyyy - HH:MM'
  return `${day}/${month}/${year} - ${hours}:${minutes}`;
}

/**
 * Extracts the hour (HH:mm) from an ISO datetime string.
 * @param {string} dateString - ISO datetime string (e.g. '2025-10-24T19:15:12')
 * @param {boolean} [showSeconds=false] - If true, include seconds.
 * @returns {string} - 'HH:mm' or 'HH:mm:ss', or empty string if invalid.
 */
export function formatHourByIso(dateString, showSeconds = false) {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  if (showSeconds) {
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${hours}:${minutes}:${seconds}`
  }

  return `${hours}:${minutes}`
}

/**
 * Maps a document type to a corresponding key.
 * @param {string} documentType - The document type to map.
 * @returns {string|null} - The corresponding key or null if not found.
 * example:
 * console.log(mapDocumentTypeToKey('STATEMENT')); // Output: 'STATEMENT'
 * console.log(mapDocumentTypeToKey('CREDIT_ACQ')); // Output: 'TAX_CREDIT'
 * console.log(mapDocumentTypeToKey('CREDIT_POS')); // Output: 'TAX_CREDIT'
 * console.log(mapDocumentTypeToKey('ISP_SUMMARY')); // Output: 'ISP_SUMMARY'
 */
export function mapDocumentTypeToKey(documentType) {
  const mapping = {
    STATEMENT: 'STATEMENT',
    INVOICE: 'INVOICE',
    LEGAL_DOC: 'LEGAL_DOC',
    CREDIT_ACQ: 'TAX_CREDIT',
    CREDITO_ACQ: 'TAX_CREDIT',
    CREDIT_POS: 'TAX_CREDIT',
    CREDITO_ACQ_ISP: 'TAX_CREDIT',
    ISP_UCC: 'LEGAL_DOC',
    ISP_SUMMARY: 'STATEMENT',
  };

  return mapping[documentType] || null;
}

/**
 * Maps a document type to a corresponding TabKey.
 * @param {string} documentType - The document type to map.
 * @returns {string|null} - The corresponding tabKey or null if not found.
 * example:
 * console.log(mapDocumentTypeToTabKey('STATEMENT')); // Output: 'BANK_STATEMENTS'
 * console.log(mapDocumentTypeToTabKey('ISP_SUMMARY')); // Output: 'BANK_STATEMENTS'
 * console.log(mapDocumentTypeToTabKey('CREDIT_ACQ')); // Output: 'TAX_CREDIT'
 * console.log(mapDocumentTypeToTabKey('CREDIT_POS')); // Output: 'TAX_CREDIT'
 */
export function mapDocumentTypeToTabKey(documentType) {
  const mapping = {
    STATEMENT: 'BANK_STATEMENTS',
    INVOICE: 'INVOICES',
    LEGAL_DOC: 'OTHER_DOCUMENTS',
    ISP_UCC: 'OTHER_DOCUMENTS',
    CREDIT_ACQ: 'TAX_CREDIT',
    CREDITO_ACQ: 'TAX_CREDIT',
    CREDIT_POS: 'TAX_CREDIT',
    CREDITO_ACQ_ISP: 'TAX_CREDIT',
    ISP_SUMMARY: 'BANK_STATEMENTS',
  };

  return mapping[documentType] || null;
}

/**
 * Formats a number into a string with thousand separators, decimals, and a currency symbol.
 * Allows customization of whether to include a space between the number and the currency symbol.
 * For Italian locale with EUR currency, forces thousand separators even for 4-digit numbers.
 * @param {number} amount - The number to format.
 * @param {boolean} addSpace - Whether to include a space between the number and the currency (default: false).
 * @param {string} currency - The currency code (e.g., "EUR", "USD").
 * @param {string} locale - The locale to use for formatting (default: "it-IT").
 * @returns {string} - The formatted number as a string with the currency symbol.
 * example:
 * console.log(formatCurrency(1510.03)); // Output: "1.510,03€"
 * console.log(formatCurrency(1234567.89)); // Output: "1.234.567,89€"
 * console.log(formatCurrency(1234567.89, true)); // Output: "1.234.567,89 €"
 */

export function formatCurrency(amount, addSpace = false, currency = 'EUR', locale = 'it-IT') {
  if (isNaN(amount)) {
    return '';
  }

  // Format the number with Intl.NumberFormat
  let formattedAmount = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol',
  }).format(amount);

  // For Italian locale with EUR, add thousand separator for 4-digit numbers
  if (locale === 'it-IT' && currency === 'EUR') {
    // Match 4-digit numbers without separator (e.g., "1510,03" or "1510 ")
    formattedAmount = formattedAmount.replace(/(\d)(\d{3})(,\d{2})/, '$1.$2$3');
  }

  // Adjust the space between the number and the currency symbol
  if (!addSpace) {
    return formattedAmount.replace(/\s+/g, ''); // Remove any spaces
  }

  return formattedAmount; // Return with space by default
}

/**
 * Formats a time input string into a valid HH:mm format.
 * @param {string} value - The input value to format.
 * @returns {string} - The formatted time string.
 * example:
 * console.log(formatHourInput("1234")); // Output: "12:34"
 */

export function formatHourInput(value) {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return digits.slice(0, 2) + ':' + digits.slice(2, 4);
  return digits.slice(0, 2) + ':' + digits.slice(2, 4);
}

/**
 * Parses a documentId string and returns an object with file type flags.
 * @param {string} id - The input string (e.g. "xxx_A;yyy_S;zzz_X;")
 * @returns {object} - { files: { analyticalPDF: boolean, analyticalXLS: boolean, synteticPdf: boolean } }
 * example:
 * console.log(parseISPStatementFiles("xxx_A;yyy_S;zzz_X;")); // Output: { files: { analyticalPDF: true, analyticalXLS: true, synteticPdf: true } }
 * console.log(parseISPStatementFiles("xxx_A;yyy_S;")); // Output: { files: { analyticalPDF: true, analyticalXLS: false, synteticPdf: true } }
 */
export function parseISPStatementFiles(id) {
  const documentId = id.split(';').filter(Boolean); // Remove empty strings from trailing ;
  return {
    analyticalPDF: documentId.some((p) => p.endsWith('_A')),
    analyticalXLS: documentId.some((p) => p.endsWith('_X')),
    synteticPdf: documentId.some((p) => p.endsWith('_S')),
  };
}

/**
 * Extracts part of the string based on downloadType and format.
 * @param {string} str - The input string (e.g. "xxx_A;yyy_S;zzz_X;")
 * @param {string} downloadType - "A" (analytical) or "S" (synthetic)
 * @param {string} format - "PDF" or "XLS" - Uppercase
 * @returns {string} - The part of the string with the matching suffix, or empty string if not found
 *
 * Examples:
 * getMultipleDownloadFileStatementName("xxx_A;yyy_S;zzz_X;", "A", "PDF") // returns "xxx_A"
 * getMultipleDownloadFileStatementName("xxx_A;yyy_S;zzz_X;", "A", "XLS") // returns "zzz_X"
 * getMultipleDownloadFileStatementName("xxx_A;yyy_S;zzz_X;", "S", "PDF") // returns "yyy_S"
 */
export function getMultipleDownloadFileStatementName(str, downloadType = 'A', format = 'PDF') {
  if (!str) return '';

  let suffix = '';
  if (downloadType === 'A' && format.toUpperCase() === 'PDF') suffix = '_A';
  else if (downloadType === 'A' && format.toUpperCase() === 'XLS') suffix = '_X';
  else if (downloadType === 'S' && format.toUpperCase() === 'PDF') suffix = '_S';
  else return '';

  const part = str
    .split(';')
    .filter(Boolean)
    .find((s) => s.endsWith(suffix));

  return part || '';
}

/**
 * Extracts the base part of a documentId string before the suffix (_A, _X, _S, with or without ;)
 * @param {string} str - The input string (e.g. "xxx_A;", "xxx_A", "xxx_X;", "xxx_S")
 * @returns {string} - The base string without the suffix, or empty string if not found
 *
 * Examples:
 * getSingleDownloadFileStatementName("xxx_A;") // returns "xxx"
 * getSingleDownloadFileStatementName("xxx_X;") // returns "xxx"
 */
export function getSingleDownloadFileStatementName(str) {
  if (!str) return '';
  const match = str.match(/^(.*)(_A|_X|_S);?$/);
  return match ? match[1] : '';
}

/**
 * Returns downloadType and downloadFormat based on the suffix of the input string.
 * @param {string} documentId - The input string (e.g. "xxxx_A", "xxxx_X;", "xxxx_S;")
 * @returns {object|null} - { downloadType: "A"|"S", downloadFormat: "PDF"|"XLS" } or null if no match
 * Examples:
 * getDownloadFileStatementFormatAndType("xxx_A;") // { downloadType: "A", downloadFormat: "PDF" }
 * getDownloadFileStatementFormatAndType("xxx_X;") // { downloadType: "A", downloadFormat: "XLS" }
 * getDownloadFileStatementFormatAndType("xxx_S;") // { downloadType: "S", downloadFormat: "PDF" }
 */
export function getDownloadFileStatementFormatAndType(documentId) {
  if (/_A;?$/.test(documentId)) {
    return { downloadType: 'A', downloadFormat: 'PDF' };
  }
  if (/_X;?$/.test(documentId)) {
    return { downloadType: 'A', downloadFormat: 'XLS' };
  }
  if (/_S;?$/.test(documentId)) {
    return { downloadType: 'S', downloadFormat: 'PDF' };
  }
  if (/_ISPDDS;?$/.test(documentId)) {
    return { downloadType: null, downloadFormat: 'PDF' };
  }
  return { downloadType: null, downloadFormat: null }
}

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const normalizeDisputeCreationData = (creationDisputeData) => {
  if (!creationDisputeData) return {}

  const {
    mailboxName,
    shops,
    contact,
    contractType
  } = creationDisputeData

  // Mappa il contractType ai valori dbSourceType corretti
  const dbSourceTypeMap = {
    nexi: 'ACQUIRING_NEXI',
    mps: 'ACQUIRING_MPS',
    core: 'ACQUIRING_CORE'
  }

  return {
    eboxName: mailboxName,
    shopCodes: (shops ?? []).map(s => s.shopCode),
    eboxEmail: contact?.email,
    eboxPhoneNumber: `${contact?.phoneNumber ?? ''}`.trim(),
    dbSourceType: dbSourceTypeMap[contractType] || contractType, // Mappa o fallback al valore originale
    notifyNewClaims: !!contact?.notifyNewRequests,
    notifyClaimsTakenByBank: !!contact?.notifyTakenInCharge,
    notifyEveryProcessedClaim: !!contact?.notifyDailyReport
  }
}
/**
 * Capitalizes the first letter of each word in a string while keeping acronyms in uppercase.
 *
 * @param {string} str - The input string to be transformed.
 * @returns {string} - The transformed string with the first letter of each word capitalized or the original word if it's an acronym.
 */
export function capitalizeFirst(str) {
  if (!str) return '';

  // Lista degli acronimi da mantenere in maiuscolo (con o senza punti)
  const acronyms = [
    'srl', 's.r.l', 'spa', 's.p.a', 'sda', 's.d.a', 'asd', 'a.s.d', 'bpm', 'b.p.m'
  ];

  // Funzione per normalizzare una parola rimuovendo i punti e rendendola lowercase
  const normalize = (word) => word.replace(/\./g, '').toLowerCase();

  // Funzione per determinare se una parola è un acronimo target
  const isAcronym = (word) => {
    // Rimuove eventuale punto finale
    const cleanWord = word.replace(/\.$/, '');
    return acronyms.some(acr => normalize(cleanWord) === normalize(acr));
  };

  // Funzione per determinare se una parola è un codice che inizia con LN (es: LN00000323434)
  const isLocationCode = (word) => (
    word.toUpperCase().startsWith('LN')
  );

  return str.split(' ').map(word => {
    if (isAcronym(word)) {
      // Mantiene la parola in maiuscolo, con i punti se presenti
      return word.toUpperCase();
    }
    if (isLocationCode(word)) {
      // Mantiene i codici location così come sono
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
}

/**
 * Capitalizes country names by making the first letter of each significant word uppercase,
 * while keeping articles and prepositions in lowercase.
 *
 * @param {string} label - The country name to be capitalized.
 * @returns {string} - The country name with proper capitalization.
 * 
 * @example
 * capitalizeAllWordIntoString('STATI UNITI D'AMERICA'); // "Stati Uniti d'America"
 * capitalizeAllWordIntoString('REPUBBLICA DI SAN MARINO'); // "Repubblica di San Marino"
 * capitalizeAllWordIntoString('EMIRATI ARABI UNITI'); // "Emirati Arabi Uniti"
 * capitalizeAllWordIntoString('REGNO UNITO'); // "Regno Unito"
 * capitalizeAllWordIntoString('COSTA D'AVORIO'); // "Costa d'Avorio" (apostrofo curvo)
 * capitalizeAllWordIntoString('GUINEA-BISSAU'); // "Guinea-Bissau"
 */
export function capitalizeAllWordIntoString(label) {
  if (!label || typeof label !== 'string') return label;

  // Articoli/preposizioni da mantenere in minuscolo (supporto IT + EN/FR/ES di base)
  const articlesSet = new Set([
    'di', 'del', 'della', 'dei', 'delle', 'da', 'dal', 'dalla', 'dai', 'dalle',
    'a', 'al', 'alla', 'ai', 'alle', 'in', 'nel', 'nella', 'nei', 'nelle',
    'con', 'per', 'su', 'sul', 'sulla', 'sui', 'sulle', 'tra', 'fra',
    'e', 'ed', 'o', 'od', 'il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'uno', 'una',
    'of', 'the', 'and', 'de', 'du', 'des', 'les', 'et', 'ou',
    'y', 'el', 'los', 'las', 'por', 'para', 'en'
  ]);

  const capFirst = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

  const normalize = (s) => (
    s
      // normalizza apostrofi curvi/accents/backtick in semplice '
      .replace(/[’‘`´]/g, "'")
      // rimuove spazi prima di apostrofo: "D 'AMERICA" -> "D'AMERICA"
      .replace(/\b([A-Za-z])\s+'(?=\w)/g, "$1'")
      // compatta spazi multipli
      .replace(/\s+/g, ' ')
      .trim()
  );

  const handleApostrophe = (token) =>
    // Regola richiesta: prima dell'apostrofo lowercase, dopo capitalizza la prima lettera
    token.split("'").map((part, i) => (
      i === 0 ? part.toLowerCase() : capFirst(part)
    )).join("'");

  const handleHyphen = (token) => token.split('-').map(part => (
    part.includes("'") ? handleApostrophe(part) : capFirst(part)
  )).join('-');

  const input = normalize(label);

  const words = input.split(' ');
  return words.map((raw, i) => {
    const word = raw.toLowerCase();

    if (word.includes('-')) return handleHyphen(word);
    if (word.includes("'")) return handleApostrophe(word);

    if (i === 0) return capFirst(word);
    if (articlesSet.has(word)) return word; // articoli/preposizioni in minuscolo
    return capFirst(word);
  }).join(' ');
}

export const buildLoyaltyRedirectUrl = ({ baseUrl, merchantIdHashed, isNativeApp }) => {
  if (typeof baseUrl !== 'string') return null;

  const hasQuery = baseUrl.includes('?');
  const separator = hasQuery ? '&' : '?';

  const utmParams = {
    utm_source: 'cvm_acq',
    utm_medium: isNativeApp ? 'app' : 'web',
    utm_campaign: '2024_nexi_merchant-engagement',
    utm_term: 'consent-page',
    mid: merchantIdHashed
  };

  const queryString = new URLSearchParams(utmParams).toString();

  return `${baseUrl}${separator}${queryString}`;
};

export const getLoyaltyAdditionalData = (serviceQuickLinksData = {}) => {
  if (
    !serviceQuickLinksData ||
    typeof serviceQuickLinksData !== 'object' ||
    !serviceQuickLinksData.widgets
  ) {
    return null;
  }

  const loyalty = serviceQuickLinksData.widgets.LOYALTY;

  return loyalty?.prop || null;
};

export const translateCountry = (countryName, isoCode, language) => {
  const displayNames = new Intl.DisplayNames([language], { type: 'region' })
  let countryNameUpper = countryName.toUpperCase()
  let isoCodeFix = (isoCode === 'AR' && countryNameUpper === 'EMIRATI ARABI UNITI') ? "AE" : isoCode
  const specialRegions = [
    {
      isoCode: 'IT',
      regions: ["CAMPIONE D'ITALIA"]
    },
    {
      isoCode: 'AE',
      regions: ["ABU DHABI", "AJMAN", "DUBAI", "FUIJAYRAH", "RAS AL KHAIMAH", "SHARJAH", "UMM AL QAIWAIN"]
    },
    {
      isoCode: 'SH',
      regions: ["ASCENSION", "GOUGH", "TRISTAN DA CUNHA"]
    },
    {
      isoCode: "PT",
      regions: ["AZZORRE ISOLE", "MADEIRA"]
    },
    {
      isoCode: "ES",
      regions: ["CANARIE ISOLE", "CEUTA", "CHAFARINAS", "MELILLA", "PENON DE ALHUCEMAS", "PENON DE VELEZ DE LA GOMERA"]
    },
    {
      isoCode: "FM",
      regions: ["CAROLINE ISOLE"]
    },
    {
      isoCode: "CS",
      regions: ["CECOSLOVACCHIA"]
    },
    {
      isoCode: "IO",
      regions: ["TERRITORIO BRITANNICO OCEANO INDIANO"]
    },
    {
      isoCode: "CX",
      regions: ["ISOLA DI NATALE"]
    },
    {
      isoCode: "FR",
      regions: ["CLIPPERTON"]
    },
    {
      isoCode: "UM",
      regions: ["ISOLE MINORI STATI UNITI AMERICA", "MIDWAY ISOLE", "WAKE ISOLE"]
    },
    {
      isoCode: "SB",
      regions: ["SALOMONE ISOLE"]
    },
    {
      isoCode: "RS",
      regions: ["KOSOVO"]
    },
    {
      isoCode: "PS",
      regions: ["TERRITORI PALESTINESI"]
    },
    {
      isoCode: "PS",
      regions: ["YEMEN MERIDIONALE"]
    },
    {
      isoCode: "PZ",
      regions: ["PANAMA ZONA DEL CANALE"]
    }
  ]

  const exceptionsCountriesIsoCodes = ['URSS']
  const filterIsoCode = specialRegions.filter(reg => reg.isoCode === isoCodeFix)
  if (filterIsoCode.length > 0 && filterIsoCode[0].regions.includes(countryNameUpper)) return countryNameUpper;
  return exceptionsCountriesIsoCodes.includes(isoCodeFix) ? countryNameUpper : displayNames.of(isoCodeFix)
}

export const supportedMultilanguageLocales = ['it', 'en', 'es', 'de', 'fr', 'el'];

export const normalizeAppLocale = (locale, fallbackLocale = 'it') => {
  const normalizedLocale = typeof locale === 'string'
    ? locale.toLowerCase().split(/[-_]/)[0]
    : '';

  return supportedMultilanguageLocales.includes(normalizedLocale) ? normalizedLocale : fallbackLocale;
};

export const getDatePickerLocale = () => 'it';

export const getLocalizedCountryOption = (country, language) => {
  const localizedName = country?.name && country?.isoCode
    ? translateCountry(country.name, country.isoCode, normalizeAppLocale(language))
    : country?.name;

  return {
    ...country,
    label: capitalizeAllWordIntoString(localizedName),
    value: country?.isoCode
  };
};

//This util is for delete word in bracket, for example hello(world) becomes hello
export const removeBrackets = (word) => {

  let newword = word;

  if (word.includes('(')) {
    newword = word.split('(')[0]
  }

  return newword;
}

/**
 * Check fiscalCode from Info object
 *
 * @param {obj} info - es:
 * {
 *  birthday:'21/10/1997' // dd/mm/aaaa
 *  codUifBirthNation: '086' // uif code birth nation (if italy 086)
 *  birthplace: 'Milano' // City name if is italian city else bithNation
 *  birthplaceProvincia: 'MI' // Province code of birthPlace. If is abroad this value is 'EE'
 *  name: 'Elia'
 *  surname: 'Minozzi'
 *  gender: 'M' // 'M' or 'F'
 *  cf: 'MNZLEI97R21F205N' // fiscalCode
 * }
 * @returns {array} - Fields with errors
 */
export const checkFiscalCode = (info) => {
  let birthDayArray = info.birthday.split('/')

  let formData = {
    name: info.name.toUpperCase(),
    surname: info.surname.toUpperCase(),
    gender: info.gender,
    day: parseInt(birthDayArray[0]),
    month: parseInt(birthDayArray[1]),
    year: parseInt(birthDayArray[2]),
    birthplace: info.birthplace.toUpperCase(),
    birthplaceProvincia: info.birthplaceProvincia,
  }

  let errors = []

  if (info.cf.length === 16) {

    let calculatedData = null

    try {
      calculatedData = CodiceFiscale.computeInverse(info.cf.toUpperCase())
    }
    catch (e) {
      errors.push('cf')
      return errors
    }
    if (calculatedData) {
      for (let [key, value] of Object.entries(calculatedData)) {
        if (key !== 'birthday' && key !== 'cf') {

          let field = key
          if (key === 'name' || key === 'surname') {
            let vowels = /[aeiouAEIOU]+/gm
            let vowelsField = []
            let consonantsField = []

            if (formData[field].length < 3) {
              if (formData[field].toUpperCase() + 'X' !== value) {
                errors.push(field)
              }
            } else {
              for (let i = 0; i < formData[field].length; i++) {
                if (formData[field][i] !== ' ') {
                  if (!(formData[field][i].match(vowels))) {
                    consonantsField.push(formData[field][i].toUpperCase())
                  } else {
                    vowelsField.push(formData[field][i].toUpperCase())
                  }
                }
              }


              if (consonantsField.length === 3) {
                if (consonantsField.join('') !== value) {
                  errors.push(field)
                }
              } else if (consonantsField.length > 3) {
                if (key === 'name') {
                  if (consonantsField[0] + consonantsField[2] + consonantsField[3] !== value) {
                    errors.push(field)
                  }
                }
                if (key === 'surname') {
                  if (consonantsField[0] + consonantsField[1] + consonantsField[2] !== value) {
                    errors.push(field)
                  }
                }

              } else if (consonantsField.length < 3) {
                let mixedField = consonantsField.join('')
                for (let i = 0; mixedField.length < 3; i++) {
                  mixedField = mixedField + vowelsField[i]
                }
                if (mixedField !== value) {
                  errors.push(field)
                }
              }
            }

          } else if (key === 'year' || key === 'day' || key === 'month') {
            if (value !== formData[field]) {
              if (key === 'year') {
                if ((value - 100) !== formData[field]) {
                  errors.push('birthDate')
                }
              } else {
                errors.push('birthDate')
              }
            }
          } else if (key === 'birthplace') {
            //console.log(formData[field],' --- ',value)
            if (formData[field].indexOf('\'') !== -1 && value !== formData[field]) {
              let position = formData[field].indexOf('\'') + 1
              if (formData[field][position] === ' ') {
                let newString = formData[field].slice(0, position) + formData[field].slice(position + 1)
                if (value !== newString) {
                  if ((newString !== "MAZZARRA'SANT' ANDREA" && value !== "MAZZARRA' SANT'ANDREA") && (newString !== "SANT'ILARIO D' ENZA" && value !== "SANT'ILARIO D'ENZA")) {
                    errors.push('birthplace')
                  }
                }
              } else {
                if (value !== formData[field]) {
                  errors.push('birthplace')
                }
              }
            } else {
              if (value.includes('MAURIZIO')) {
                value = value.replace('MAURIZIO', 'MAURITIUS')
              }
              if (value.includes('SEICELLE')) {
                value = value.replace('SEICELLE', 'SEYCHELLES')
              }
              if (formData[field] === 'RUSSIA (FEDERAZIONE DI)' && value === 'RUSSIA=FEDERAZIONE RUSSA') {
                value = 'RUSSIA (FEDERAZIONE DI)'
              }

              if (value === 'SAMONE' && formData[field] === 'SAMONE(TO)') {
                value = 'SAMONE(TO)'
              }

              if (value === 'LORENZANA' && formData[field] === 'CRESPINA LORENZANA') {
                value = 'CRESPINA LORENZANA'
              }

              if (value === 'CORIGLIANO CALABRO' && formData[field] === 'CORIGLIANO-ROSSANO') {
                value = 'CORIGLIANO-ROSSANO'
              }

              if (value === 'UNIONE REPUBBLICHE SOCIALISTE SOVIETICHE') {
                let URSSArray = [
                  'ARMENIA',
                  'AZERBAIGIAN',
                  'BIELORUSSIA',
                  'ESTONIA',
                  'GEORGIA',
                  'KAZAKISTAN',
                  'KIRGHIZISTAN',
                  'LETTONIA',
                  'LITUANIA',
                  'MOLDAVIA',
                  'RUSSIA (FEDERAZIONE DI)',
                  'TAGIKISTAN',
                  'TURKMENISTAN',
                  'UCRAINA',
                  'UZBEKISTAN',
                ]
                if (URSSArray.includes(formData[field])) {
                  value = formData[field]
                }
              }

              let booleanContains = value.includes(removeBrackets(formData[field]))
              if (info.codUifBirthNation === '086') {
                if (value !== formData[field]) {
                  errors.push('birthplace')
                }
              } else {
                if (value !== formData[field] && !booleanContains) {
                  errors.push('birthplace')
                }
              }
            }
          } else if (key === 'birthplaceProvincia') {
            // console.log(formData[field],' ---Provincia ',value)
            if (calculatedData.birthplace === 'SASSOFELTRIO' && value === 'PU' && formData[field] === 'RN') {
              value = 'RN'
            }
            if (value !== formData[field]) {
              errors.push(field)
              if (value === 'EE') {
                if (info.codUifBirthNation === '086') {
                  errors.push('birthplace')
                }
              }
            }
          } else if (value !== formData[field]) {
            errors.push(field)
          }
        }
      }

      if (errors.length !== 0) {
        errors.push('cf')
      }
    } else {
      errors.push('cf')
    }
  } else {
    errors.push('cf')
  }

  return errors
};
//  * Check doument number
//  *
//  * @param {obj} info - The information about document: documentNumber (string), codUifNation (release nation, if italy set '086'), documentType (it can be: 'CI'(identity card),'PG' (driver license),'PA' (passport),'TM' (ministerial card)), provinceCode (release province (if abroad:'EE' [es for italian province: Milano->MI]), releaseDate (es.12/03/2033))
//  * @returns {boolean} - Return true if the number is valid.
//  */
export const checkDocumentNumber = (info) => {
  const allFieldsFilledIta = Object.values(info).every(val => val !== undefined && val !== null && val !== '');
  const allFieldsFilledAbroad = Object.values({ documentNumber: info.documentNumber, codUifNation: info.codUifNation }).every(val => val !== undefined && val !== null && val !== '');

  if (info.codUifNation === '086') {
    if (!allFieldsFilledIta) return false;
  } else if (info.codUifNation !== undefined && info.codUifNation !== null && info.codUifNation !== '') {
    if (!allFieldsFilledAbroad) return false;
  } else {
    return false;
  }

  if (info.documentNumber.length > 2) { // The length must be at least 3
    let regMinistryDocument = /^\d{6}\/\d{6}$/;
    if (info.documentNumber.length === 3 || info.documentNumber.length === 4) { // if the length is 3 or 4 the chars must not be the same
      let count = 1;
      let documentNumberArray = info.documentNumber.split('')
      let letter = documentNumberArray[0];

      for (let i = 1; i < info.documentNumber.length; i++) {
        if (letter === documentNumberArray[i]) {
          count = count + 1;
        }
      }
      if (count === info.documentNumber.length) {
        return false;
      }
    }
    // if --> info.codUifNation === '086'
    if (info.codUifNation === '086' && !(regMinistryDocument.test(info.documentNumber) && info.documentType === 'CI')) {
      //let count = 1;
      let documentNumberArray = info.documentNumber.split('')
      let letter = '';
      for (let i = 0; i < documentNumberArray.length; i++) {
        letter = documentNumberArray[i];
        if (isNaN(letter) === true) { // Only for ITALY : If longer than 4 characters, it is checked that there are no sequences of 4 consecutive identical letters or 5 consecutive identical numbers
          if (documentNumberArray.length >= i + 4) {
            let docSubString = info.documentNumber.substring(i, i + 4)
            let localCount = 1;
            let localDocumentNumberArray = docSubString.split('')
            let localLetter = docSubString[0];

            for (let j = 1; j < docSubString.length; j++) {
              if (localLetter === localDocumentNumberArray[j]) {
                localCount = localCount + 1;
              }
            }
            if (localCount === docSubString.length) {
              return false;
            }
          }
        } else {
          if (documentNumberArray.length >= i + 5) {
            let docSubString = info.documentNumber.substring(i, i + 5)
            let localCount = 1;
            let localDocumentNumberArray = docSubString.split('')
            let localLetter = docSubString[0];

            for (let j = 1; j < docSubString.length; j++) {
              if (localLetter === localDocumentNumberArray[j]) {
                localCount = localCount + 1;
              }
            }
            if (localCount === docSubString.length) {
              return false;
            }

          }
        }

      }

    }
    //ONLY FOR ITALY: Identity Cards issued after January 1, 2003: The first two or last two characters must be letters, or the first two and last two characters must be letters; all other characters must be numbers. The ID number must be 9 characters long, with the exception of ID cards issued in municipalities in the provinces of Bolzano, Trento, Aosta Valley, Trentino Alto Adige, and Gorizia, for which ID cards with 8-character numbers will also be accepted.
    if (info.documentType === 'CI') {
      if (info.codUifNation === '086') {
        let specialProvincesCodes = ['TS', 'BZ', 'TN', 'AO', 'GO', 'UD', 'PN', 'CA', 'NU', 'OR', 'SS', 'SU']
        if (specialProvincesCodes.includes(info.provinceCode)) {
          if (!(info.documentNumber.length === 8 || info.documentNumber.length === 9 || regMinistryDocument.test(info.documentNumber))) {
            return false;
          }
        } else {
          if (!(info.documentNumber.length === 9 || regMinistryDocument.test(info.documentNumber))) {
            return false;
          }
        }
        if (parseInt(info.releaseDate.split('/')[2]) > 2002) {
          let documentNumberArray = info.documentNumber.split('')
          //let letter='';
          let letter0 = documentNumberArray[0];
          let letter1 = documentNumberArray[1];
          let letterEnd = documentNumberArray[documentNumberArray.length - 1];
          let letterFirstEnd = documentNumberArray[documentNumberArray.length - 2];

          if ((isNaN(letter0) === true && isNaN(letter1) === true) || (isNaN(letterEnd) === true && isNaN(letterFirstEnd) === true)) {
            let subStringCodeNumber = ''
            if ((isNaN(letter0) === true && isNaN(letter1) === true) && (isNaN(letterEnd) === true && isNaN(letterFirstEnd) === true)) {
              subStringCodeNumber = info.documentNumber.substring(2, info.documentNumber.length - 2)
            } else if ((isNaN(letter0) === true && isNaN(letter1) === true)) {
              subStringCodeNumber = info.documentNumber.substring(2, info.documentNumber.length)
            } else if (isNaN(letterEnd) === true && isNaN(letterFirstEnd) === true) {
              subStringCodeNumber = info.documentNumber.substring(0, info.documentNumber.length - 2)
            }
            if (isNaN(subStringCodeNumber) === true) {
              if (!regMinistryDocument.test(info.documentNumber) || (regMinistryDocument.test(info.documentNumber) && parseInt(info.releaseDate.split('/')[2]) < 2023)) {
                return false;
              }
            }

          } else {
            if (!regMinistryDocument.test(info.documentNumber) || (regMinistryDocument.test(info.documentNumber) && parseInt(info.releaseDate.split('/')[2]) < 2023)) {
              return false;
            }
          }
        }
      }
    }
  } else {
    return false;
  }
  return true;
}

export const utmConverter = async (utmParam) => {
  // Convert string to ArrayBuffer
  const encoder = new TextEncoder();
  const data = encoder.encode(utmParam);

  // Calculate SHA-256 hash using Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

export const alpha3ToAlpha2 = {
  AFG: 'AF', ALB: 'AL', DZA: 'DZ', ASM: 'AS', AND: 'AD', AGO: 'AO', AIA: 'AI', ATA: 'AQ', ATG: 'AG',
  ARG: 'AR', ARM: 'AM', ABW: 'AW', AUS: 'AU', AUT: 'AT', AZE: 'AZ', BHS: 'BS', BHR: 'BH', BGD: 'BD',
  BRB: 'BB', BLR: 'BY', BEL: 'BE', BLZ: 'BZ', BEN: 'BJ', BMU: 'BM', BTN: 'BT', BOL: 'BO', BES: 'BQ',
  BIH: 'BA', BWA: 'BW', BVT: 'BV', BRA: 'BR', IOT: 'IO', BRN: 'BN', BGR: 'BG', BFA: 'BF', BDI: 'BI',
  CPV: 'CV', KHM: 'KH', CMR: 'CM', CAN: 'CA', CYM: 'KY', CAF: 'CF', TCD: 'TD', CHL: 'CL', CHN: 'CN',
  CXR: 'CX', CCK: 'CC', COL: 'CO', COM: 'KM', COG: 'CG', COD: 'CD', COK: 'CK', CRI: 'CR', CIV: 'CI',
  HRV: 'HR', CUB: 'CU', CUW: 'CW', CYP: 'CY', CZE: 'CZ', DNK: 'DK', DJI: 'DJ', DMA: 'DM', DOM: 'DO',
  ECU: 'EC', EGY: 'EG', SLV: 'SV', GNQ: 'GQ', ERI: 'ER', EST: 'EE', SWZ: 'SZ', ETH: 'ET', FLK: 'FK',
  FRO: 'FO', FJI: 'FJ', FIN: 'FI', FRA: 'FR', GER: 'DE', GUF: 'GF', PYF: 'PF', ATF: 'TF', GAB: 'GA',
  GMB: 'GM', GEO: 'GE', DEU: 'DE', GHA: 'GH', GIB: 'GI', GRC: 'GR', GRL: 'GL', GRD: 'GD', GLP: 'GP',
  GTM: 'GT', GGY: 'GG', GIN: 'GN', GNB: 'GW', GUY: 'GY', HTI: 'HT', HMD: 'HM', VAT: 'VA', HND: 'HN',
  HKG: 'HK', HUN: 'HU', ISL: 'IS', IND: 'IN', IDN: 'ID', IRN: 'IR', IRQ: 'IQ', IRL: 'IE', IMN: 'IM',
  ISR: 'IL', ITA: 'IT', JAM: 'JM', JPN: 'JP', JEY: 'JE', JOR: 'JO', KAZ: 'KZ', KEN: 'KE', KIR: 'KI',
  PRK: 'KP', KOR: 'KR', KWT: 'KW', KGZ: 'KG', LAO: 'LA', LVA: 'LV', LBN: 'LB', LSO: 'LS', LBR: 'LR',
  LBY: 'LY', LIE: 'LI', LTU: 'LT', LUX: 'LU', MAC: 'MO', MDG: 'MG', MWI: 'MW', MYS: 'MY', MDV: 'MV',
  MLI: 'ML', MLT: 'MT', MHL: 'MH', MTQ: 'MQ', MRT: 'MR', MUS: 'MU', MYT: 'YT', MEX: 'MX', FSM: 'FM',
  MDA: 'MD', MCO: 'MC', MNG: 'MN', MNE: 'ME', MSR: 'MS', MAR: 'MA', MOZ: 'MZ', MMR: 'MM', NAM: 'NA',
  NRU: 'NR', NPL: 'NP', NLD: 'NL', NCL: 'NC', NZL: 'NZ', NIC: 'NI', NER: 'NE', NGA: 'NG', NIU: 'NU',
  NFK: 'NF', MKD: 'MK', MNP: 'MP', NOR: 'NO', OMN: 'OM', PAK: 'PK', PLW: 'PW', PSE: 'PS', PAN: 'PA',
  PNG: 'PG', PRY: 'PY', PER: 'PE', PHL: 'PH', PCN: 'PN', POL: 'PL', PRT: 'PT', PRI: 'PR', QAT: 'QA',
  REU: 'RE', ROU: 'RO', RUS: 'RU', RWA: 'RW', BLM: 'BL', SHN: 'SH', KNA: 'KN', LCA: 'LC', MAF: 'MF',
  SPM: 'PM', VCT: 'VC', WSM: 'WS', SMR: 'SM', STP: 'ST', SAU: 'SA', SEN: 'SN', SRB: 'RS', SYC: 'SC',
  SLE: 'SL', SGP: 'SG', SXM: 'SX', SVK: 'SK', SVN: 'SI', SLB: 'SB', SOM: 'SO', ZAF: 'ZA', SGS: 'GS',
  SSD: 'SS', ESP: 'ES', LKA: 'LK', SDN: 'SD', SUR: 'SR', SJM: 'SJ', SWE: 'SE', CHE: 'CH', SYR: 'SY',
  TWN: 'TW', TJK: 'TJ', TZA: 'TZ', THA: 'TH', TLS: 'TL', TGO: 'TG', TKL: 'TK', TON: 'TO', TTO: 'TT',
  TUN: 'TN', TUR: 'TR', TKM: 'TM', TCA: 'TC', TUV: 'TV', UGA: 'UG', UKR: 'UA', ARE: 'AE', GBR: 'GB',
  USA: 'US', UMI: 'UM', URY: 'UY', UZB: 'UZ', VUT: 'VU', VEN: 'VE', VNM: 'VN', VGB: 'VG', VIR: 'VI',
  WLF: 'WF', ESH: 'EH', YEM: 'YE', ZMB: 'ZM', ZWE: 'ZW', GUM: 'GU', ROL: 'RO', ROM: 'RO'
}

export function getCountryLabelFromAlpha3(alpha3, regionNames) {
  const alpha2 = alpha3ToAlpha2[alpha3]
  if (alpha2) {
    try {
      return regionNames.of(alpha2) || alpha3
    } catch {
      return alpha3
    }
  }
  return alpha3
}
//  * Check if twovObject are Same
//  *
//  * @param {obj} a - First obj
//  * @param {obj} b - Second obj
//  * @returns {boolean} - Return true if the obj are true
//  */
export const isEqualObj = (a, b) => {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (a === null || b === null) return false;

  if (typeof a !== 'object') return false;

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (let key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqualObj(a[key], b[key])) return false;
  }

  return true;
}

/**
 * Converts an HTML element to PDF and downloads it.
 * @param {string} elementId - The ID of the HTML element to convert (default: 'wrapper').
 * @param {string} fileName - The name of the PDF file to download (default: 'file.pdf').
 * @param {object} options - Configuration options for the PDF generation.
 * @param {number} options.imgWidth - Width of the image in the PDF (default: 185mm).
 * @param {number} options.pageHeight - Height of each page (default: 295mm).
 * @param {number} options.marginLeft - Left margin (default: 12mm).
 * @param {boolean} options.useCORS - Enable CORS for html2canvas (default: true).
 * @param {boolean} options.allowTaint - Allow taint for html2canvas (default: true).
 * @returns {Promise<void>} - A promise that resolves when the PDF is downloaded.
 * 
 * @example
 * // Basic usage
 * downloadPdfDocument('myElement', 'report.pdf');
 * 
 * // With custom options
 * downloadPdfDocument('chart', 'chart.pdf', {
 *   imgWidth: 200,
 *   pageHeight: 280,
 *   marginLeft: 10
 * });
 */
export const downloadPdfDocument = async (
  elementId = 'wrapper',
  fileName = 'file.pdf',
  options = {}
) => {
  // Destructure options with defaults
  const {
    imgWidth = 185,
    pageHeight = 295,
    marginLeft = 12,
    marginTop = 20,
    useCORS = true,
    allowTaint = true
  } = options;

  // Get the HTML element
  const input = document.getElementById(elementId);
  if (!input) {
    throw new Error(`Element with ID '${elementId}' not found`);
  }

  // Import html2canvas and jsPDF dynamically (if not already imported)
  const html2canvas = (await import('html2canvas')).default;
  const { jsPDF } = await import('jspdf');

  // Convert HTML element to canvas
  const canvas = await html2canvas(input, { useCORS, allowTaint });

  // Calculate dimensions
  let imgHeight = canvas.height * imgWidth / canvas.width;
  let heightLeft = imgHeight;

  // Determine image format based on platform
  const isNativeApp = Capacitor.isNativePlatform();
  const imageFormat = isNativeApp ? 'JPEG' : 'PNG';
  const imgData = canvas.toDataURL(`image/${imageFormat.toLowerCase()}`);

  // Create PDF document
  const doc = new jsPDF('p', 'mm', 'a4');
  let position = 0;

  // Add first page
  doc.addImage(imgData, imageFormat, marginLeft, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  // Add additional pages if content exceeds one page
  while (heightLeft >= 0) {
    position = heightLeft - imgHeight;
    doc.addPage();
    doc.addImage(imgData, imageFormat, marginLeft, position, imgWidth, imgHeight + 15);
    heightLeft -= pageHeight;
  }

  // Generate blob and download
  const blob = doc.output('blob');
  await downloadFile(blob, fileName.replace('.pdf', ''), 'application/pdf');
};

export const plainAxios = axiosBase.create()

export const usePlainAxios = async (data) => {
  if (Capacitor.isNativePlatform()) {
    let urlWithParams
    if (data.baseURL) {
      urlWithParams = `${data.baseURL}${data.url.charAt(0) === '/' ? '' : '/'}${data.url}`
    } else {
      urlWithParams = `${data.url}`
    }
    if (data.params && Object.keys(data.params).length !== 0) {
      const keys = Object.keys(data.params)
      const newUrl = urlWithParams.includes('?') ? `${urlWithParams}&` : `${urlWithParams}?`
      const params = new URLSearchParams()
      for (let i = 0; i < keys.length; i++) {
        params.append(keys[i], data.params[keys[i]])
      }
      urlWithParams = `${newUrl}${params.toString()}`
    }
    return new Promise((resolve, reject) => {
      const requestObj = {
        method: data.method.toUpperCase(),
        url: urlWithParams,
        headers: {
          Origin: process.env.APPLICATION_URL, Referrer: process.env.APPLICATION_URL
        },
        responseType: data.responseType,
        credentials: data.withCredentials
      }
      if (data.method.toUpperCase() === 'PUT' || data.method.toUpperCase() === 'POST') {
        requestObj.data = data.data
      }
      CapacitorHttp.request(requestObj)
        .then(response => {
          const newResponse = {
            data: response.data,
            status: response.status,
            statusText: response.statusText ?? '',
            headers: response.headers,
            config: data,
            request: { ...data }
          }
          //interceptResponseAndSetJwe(newResponse)
          // eslint-disable-next-line prefer-promise-reject-errors
          resolve({
            isAxiosError: false,
            ...newResponse
          })
        })
        .catch(err => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            isAxiosError: true,
            response: {
              data: err?.error ?? null,
              status: err?.status ?? 500,
              statusText: err?.message ?? 'CapacitorHttp Error',
              headers: err?.headers ?? {},
              config: data
            }
          })
        })
    })
  }

  const newData = await plainAxios(data)
  return newData;
}
